<template>
  <div class="application-pdf-editor">
    <div v-if="loading" class="editor-status" role="status" aria-live="polite">
      <v-progress-circular size="30" width="3" indeterminate></v-progress-circular>
      <div>
        <strong>Pripravujem editovateľnú žiadosť</strong>
        <span>Načítavam strany a formulárové polia…</span>
      </div>
    </div>
    <div v-else-if="error" class="editor-status editor-error" role="alert">
      <strong>Žiadosť sa nepodarilo otvoriť na editáciu.</strong>
      <span>{{ error }}</span>
    </div>
    <div v-show="!loading && !error" class="editor-scroll">
      <div
        v-for="page in pages"
        :key="page.number"
        class="editor-page"
        :data-page-number="page.number"
        :style="{ width: `${page.width}px`, height: `${page.height}px` }"
      >
        <canvas aria-hidden="true"></canvas>
        <div class="editor-fields">
          <template v-for="field in page.fields">
            <textarea
              v-if="field.type === 'text' && field.multiLine"
              :key="field.id"
              class="pdf-form-field pdf-text-field pdf-textarea"
              :aria-label="field.name"
              :maxlength="field.maxLength || null"
              :style="field.style"
              :value="fieldValue(field.name)"
              @input="updateValue(field.name, $event.target.value)"
            ></textarea>
            <input
              v-else-if="field.type === 'text'"
              :key="field.id"
              type="text"
              class="pdf-form-field pdf-text-field"
              :aria-label="field.name"
              :maxlength="field.maxLength || null"
              :style="field.style"
              :value="fieldValue(field.name)"
              @input="updateValue(field.name, $event.target.value)"
            >
            <input
              v-else-if="field.type === 'checkbox'"
              :key="field.id"
              type="checkbox"
              class="pdf-form-field pdf-checkbox-field"
              :aria-label="field.name"
              :style="field.style"
              :checked="Boolean(fieldValue(field.name))"
              @change="updateValue(field.name, $event.target.checked)"
            >
            <input
              v-else-if="field.type === 'radio'"
              :key="field.id"
              type="radio"
              class="pdf-form-field pdf-radio-field"
              :name="field.name"
              :aria-label="field.name"
              :style="field.style"
              :value="field.exportValue"
              :checked="fieldValue(field.name) === field.exportValue"
              @change="updateValue(field.name, field.exportValue)"
            >
            <select
              v-else-if="field.type === 'choice'"
              :key="field.id"
              class="pdf-form-field pdf-choice-field"
              :aria-label="field.name"
              :style="field.style"
              :multiple="field.multiSelect"
              :value="fieldValue(field.name)"
              @change="updateChoice(field, $event)"
            >
              <option
                v-for="option in field.options"
                :key="`${field.id}-${option.value}`"
                :value="option.value"
              >{{ option.label }}</option>
            </select>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as pdfjs from "pdfjs-dist/webpack";
import {saveEditedApplicationPdf} from "@/utils/applicationAiPdf";

const PAGE_SCALE = 1.12;

function initialAnnotationValue(annotation) {
  if (annotation.checkBox) {
    return Boolean(annotation.fieldValue && annotation.fieldValue !== "Off");
  }
  if (annotation.radioButton) return annotation.fieldValue || "";
  if (annotation.fieldType === "Ch") {
    const selected = Array.isArray(annotation.fieldValue)
      ? annotation.fieldValue
      : [annotation.fieldValue || ""];
    return annotation.multiSelect ? selected : selected[0];
  }
  return annotation.fieldValue || "";
}

function fieldType(annotation) {
  if (annotation.fieldType === "Tx") return "text";
  if (annotation.fieldType === "Ch") return "choice";
  if (annotation.fieldType === "Btn" && annotation.checkBox) return "checkbox";
  if (annotation.fieldType === "Btn" && annotation.radioButton) return "radio";
  return null;
}

function fieldOptions(annotation) {
  return (annotation.options || []).map((option) => ({
    value: option.exportValue,
    label: option.displayValue || option.exportValue,
  }));
}

export default {
  name: "ApplicationPdfEditor",
  props: {
    pdfBlob: {
      type: Blob,
      required: true,
    },
  },
  data: () => ({
    loading: true,
    error: null,
    pages: [],
    values: {},
    fieldTypes: {},
    pdfDocument: null,
    loadingTask: null,
  }),
  async mounted() {
    await this.loadPdf();
  },
  beforeDestroy() {
    if (this.loadingTask) this.loadingTask.destroy();
    if (this.pdfDocument) this.pdfDocument.destroy();
  },
  methods: {
    fieldValue(name) {
      return this.values[name];
    },
    updateValue(name, value) {
      this.$set(this.values, name, value);
      this.$emit("dirty");
    },
    updateChoice(field, event) {
      const value = field.multiSelect
        ? Array.from(event.target.selectedOptions).map((option) => option.value)
        : event.target.value;
      this.updateValue(field.name, value);
    },
    annotationStyle(annotation, viewport) {
      const rect = viewport.convertToViewportRectangle(annotation.rect);
      const left = Math.min(rect[0], rect[2]);
      const top = Math.min(rect[1], rect[3]);
      const width = Math.abs(rect[0] - rect[2]);
      const height = Math.abs(rect[1] - rect[3]);
      return {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
        fontSize: `${Math.max(7, Math.min(12, height * 0.62))}px`,
      };
    },
    async loadPdf() {
      this.loading = true;
      this.error = null;
      try {
        const bytes = new Uint8Array(await this.pdfBlob.arrayBuffer());
        this.loadingTask = pdfjs.getDocument({data: bytes});
        this.pdfDocument = await this.loadingTask.promise;
        const pages = [];

        for (let pageNumber = 1; pageNumber <= this.pdfDocument.numPages; pageNumber += 1) {
          const page = await this.pdfDocument.getPage(pageNumber);
          const viewport = page.getViewport({scale: PAGE_SCALE});
          const annotations = await page.getAnnotations({intent: "display"});
          const fields = annotations
            .filter((annotation) => (
              annotation.subtype === "Widget"
              && annotation.fieldName
              && !annotation.readOnly
              && fieldType(annotation)
            ))
            .map((annotation, annotationIndex) => {
              const type = fieldType(annotation);
              const exportValue = annotation.buttonValue || annotation.exportValue || "";
              if (!Object.prototype.hasOwnProperty.call(this.values, annotation.fieldName)) {
                this.$set(this.values, annotation.fieldName, initialAnnotationValue(annotation));
                this.$set(this.fieldTypes, annotation.fieldName, type);
              }
              return {
                id: `${pageNumber}-${annotationIndex}-${annotation.fieldName}`,
                name: annotation.fieldName,
                type,
                exportValue,
                options: fieldOptions(annotation),
                multiLine: Boolean(annotation.multiLine),
                multiSelect: Boolean(annotation.multiSelect),
                maxLength: annotation.maxLen || null,
                style: this.annotationStyle(annotation, viewport),
              };
            });

          pages.push({
            number: pageNumber,
            page,
            viewport,
            fields,
            width: viewport.width,
            height: viewport.height,
          });
        }

        this.pages = pages;
        await this.$nextTick();
        for (const pageData of this.pages) {
          await this.renderPage(pageData);
        }
        this.loading = false;
        this.$emit("ready");
      } catch (error) {
        this.error = error?.message || "PDF formulár nie je možné načítať.";
        this.$emit("error", error);
      } finally {
        this.loading = false;
      }
    },
    async renderPage(pageData) {
      const pageElement = this.$el.querySelector(
        `[data-page-number="${pageData.number}"]`,
      );
      const canvas = pageElement.querySelector("canvas");
      const outputScale = window.devicePixelRatio || 1;
      canvas.width = Math.floor(pageData.viewport.width * outputScale);
      canvas.height = Math.floor(pageData.viewport.height * outputScale);
      canvas.style.width = `${pageData.viewport.width}px`;
      canvas.style.height = `${pageData.viewport.height}px`;
      const context = canvas.getContext("2d", {alpha: false});
      await pageData.page.render({
        canvasContext: context,
        viewport: pageData.viewport,
        transform: outputScale === 1 ? null : [outputScale, 0, 0, outputScale, 0, 0],
        annotationMode: pdfjs.AnnotationMode.ENABLE,
      }).promise;
    },
    async exportPdf() {
      if (this.loading || this.error) {
        throw new Error("Editovateľná žiadosť ešte nie je pripravená.");
      }
      const fieldValues = Object.entries(this.values).map(([name, value]) => ({
        name,
        value,
      }));
      const {blob} = await saveEditedApplicationPdf(this.pdfBlob, fieldValues);
      return blob;
    },
  },
};
</script>

<style lang="scss" scoped>
.application-pdf-editor {
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
  background: #dce3eb;
}

.editor-scroll {
  position: absolute;
  inset: 0;
  overflow: auto;
  padding: 18px;
}

.editor-page {
  position: relative;
  margin: 0 auto 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 3px 14px rgba(29, 45, 68, 0.2);

  canvas,
  .editor-fields {
    position: absolute;
    inset: 0;
  }
}

.pdf-form-field {
  position: absolute;
  z-index: 1;
  box-sizing: border-box;
  margin: 0;
  border: 1px solid rgba(45, 126, 235, 0.5);
  border-radius: 2px;
  outline: none;
  background: rgba(255, 255, 255, 0.94);
  color: #101a2a;
  font-family: Arial, sans-serif;
  line-height: 1.05;
  transition: border-color 120ms ease, box-shadow 120ms ease, background 120ms ease;

  &:hover {
    border-color: #2d7eeb;
  }

  &:focus {
    z-index: 2;
    border-color: #156fe5;
    background: #fff;
    box-shadow: 0 0 0 2px rgba(21, 111, 229, 0.2);
  }
}

.pdf-text-field,
.pdf-choice-field {
  padding: 1px 3px;
}

.pdf-textarea {
  resize: none;
}

.pdf-checkbox-field,
.pdf-radio-field {
  padding: 0;
  accent-color: #176edb;
  cursor: pointer;
}

.editor-status {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 13px;
  padding: 24px;
  background: #eef3f8;
  color: #17304f;

  > div,
  &.editor-error {
    display: flex;
    flex-direction: column;
  }

  strong {
    font-size: 13px;
    font-weight: 800;
  }

  span {
    margin-top: 3px;
    color: #667890;
    font-size: 11px;
  }
}

.editor-error {
  align-items: center;
  color: #8a3434;
  text-align: center;
}
</style>
